import type React from "react";
import { memo, useMemo, useState } from "react";
import { animated, config, useSpring } from "@react-spring/web";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import styles from "./BpsAdminSidemenu.module.scss";
import BpsAdminSidemenuButton from "./BpsAdminSidemenuButton";
import { CloseMobileMenuIcon, LogOutIcon } from "../../../assets/icons";
import { ApplicationRoutes } from "../../../routes";
import { logOut } from "../../../stores/slices/current-user-slice";
import { TenantLogo } from "../../../tenant/branding/TenantLogo";
import { useTenant } from "../../../tenant/branding/useTenant";
import { BPS_ADMIN_MENU } from "../BpsAdminMenu.constant";

type Props = {
	mobileMenuOpen: boolean;
	onMobileMenuClick: () => void;
};

const BpsAdminSideMenu: React.FC<Props> = memo((props: Props) => {
	const { mobileMenuOpen, onMobileMenuClick } = props;

	const dispatch = useDispatch();
	const navigator = useNavigate();
	const { logoWithText, name } = useTenant();

	const [openSubMenu, setOpenSubMenu] = useState<string | null>(null);

	const logout = () => {
		dispatch(logOut());
		navigator(`/${ApplicationRoutes.ADMIN_SECRET}`);
	};

	const menuStyles = useSpring({
		from: {
			transform: mobileMenuOpen ? "translateX(-100%)" : "translateX(0%)",
		},
		to: {
			transform: mobileMenuOpen ? "translateX(0%)" : "translateX(-100%)",
		},
		config: config.gentle,
	});

	const executeWrapper = useMemo(
		() => (func: () => void) => {
			func();
			onMobileMenuClick();
		},
		[onMobileMenuClick],
	);

	const handleToggleSubMenu = (text: string, isOpen: boolean) => {
		setOpenSubMenu(isOpen ? text : null);
	};

	const renderButtons = (wrapper?: (func: () => void) => void) =>
		BPS_ADMIN_MENU.map(item => (
			<BpsAdminSidemenuButton
				key={item.text}
				item={item}
				isOpen={openSubMenu === item.text}
				onToggleSubMenu={handleToggleSubMenu}
				executeWrapper={wrapper}
			/>
		));

	const logoutButton = (
		<button className={styles.logoutButton} onClick={logout}>
			<span className={styles.text}>Logout</span>
			<LogOutIcon />
		</button>
	);

	return (
		<>
			<div className={styles.container}>
				<div className={styles.logoContainer}>
					<a href={`/${ApplicationRoutes.ADMIN_BASE}/${ApplicationRoutes.DASHBOARD}`}>
						<TenantLogo logo={logoWithText} alt={name} className={styles.logo} />
					</a>
				</div>
				{renderButtons()}
				{logoutButton}
			</div>
			<animated.div style={menuStyles} className={styles.mobileMenuContainer}>
				<div className={styles.innerContainer}>
					<div className={styles.topbar}>
						<TenantLogo logo={logoWithText} alt={name} className={styles.logo} />
						<CloseMobileMenuIcon onClick={onMobileMenuClick} style={{ color: "#42577A" }} />
					</div>
					{renderButtons(executeWrapper)}
					{logoutButton}
				</div>
			</animated.div>
		</>
	);
});

export default BpsAdminSideMenu;
